import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Box, CircularProgress, Typography } from '@mui/material';

interface Location {
  latitude: number;
  longitude: number;
  address?: string;
}

interface Waypoint {
  name: string;
  latitude: number;
  longitude: number;
  order: number;
}

interface MapPreviewProps {
  location: Location;
  waypoints?: Waypoint[];
  height?: number;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const MapPreview: React.FC<MapPreviewProps> = ({
  location,
  waypoints = [],
  height = 400,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const markersRef = useRef<google.maps.Marker[]>([]);

  useEffect(() => {
    const initMap = async () => {
      if (!mapRef.current) return;

      try {
        const loader = new Loader({
          apiKey: GOOGLE_MAPS_API_KEY,
          version: 'weekly',
          libraries: ['places'],
        });

        // @ts-ignore - Loader type definitions may be incomplete
        await loader.load();

        const mapInstance = new google.maps.Map(mapRef.current, {
          center: { lat: location.latitude, lng: location.longitude },
          zoom: 13,
          mapTypeControl: true,
          streetViewControl: false,
        });

        setMap(mapInstance);
        setLoading(false);
      } catch (err) {
        console.error('Error loading map:', err);
        setError('Failed to load map. Check Google Maps API key.');
        setLoading(false);
      }
    };

    initMap();
  }, []);

  useEffect(() => {
    if (!map) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    // Add main location marker
    const mainMarker = new google.maps.Marker({
      position: { lat: location.latitude, lng: location.longitude },
      map,
      title: location.address || 'Hunt Location',
      label: {
        text: 'H',
        color: 'white',
        fontWeight: 'bold',
      },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#2E7D32',
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2,
      },
    });

    markersRef.current.push(mainMarker);

    // Add waypoint markers
    waypoints.forEach((waypoint, index) => {
      const marker = new google.maps.Marker({
        position: { lat: waypoint.latitude, lng: waypoint.longitude },
        map,
        title: waypoint.name,
        label: {
          text: (index + 1).toString(),
          color: 'white',
          fontWeight: 'bold',
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#FF6F00',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2,
        },
      });

      markersRef.current.push(marker);

      // Add info window
      const infoWindow = new google.maps.InfoWindow({
        content: `<div><strong>${waypoint.name}</strong><br/>Stop ${index + 1}</div>`,
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });
    });

    // Fit bounds to show all markers
    if (waypoints.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: location.latitude, lng: location.longitude });
      waypoints.forEach((wp) => {
        bounds.extend({ lat: wp.latitude, lng: wp.longitude });
      });
      map.fitBounds(bounds);
    } else {
      map.setCenter({ lat: location.latitude, lng: location.longitude });
      map.setZoom(13);
    }
  }, [map, location, waypoints]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <Typography color="textSecondary">
          Google Maps API key not configured
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'error.light',
          borderRadius: 1,
        }}
      >
        <Typography color="error.dark">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 1, overflow: 'hidden' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

export default MapPreview;
